'use client';
import Image from '@/components/Image';
import { env } from '@/utils/env';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import React from 'react';

interface GuideItem {
  path: string;
  image: string;
  titleKey: string;
  descriptionKey: string;
}

const ChooseEyeglassesClient: React.FC = () => {
  const t = useTranslations('chooseEyeglasses');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const frameSizeImages = {
    en: '/static-page/guide-tips/how03.jpg',
    de: '/static-page/guide-tips/how03-de.png',
    fr: '/static-page/guide-tips/how03-fr.jpg',
  };
  const prescriptionImages = {
    en: '/static-page/guide-tips/how04.jpg',
    de: '/static-page/guide-tips/how04-de.png',
    fr: '/static-page/guide-tips/how04-fr.jpg',
  };
  const guideItems: GuideItem[] = [
    {
      path: '/guide-tips/measure-PD',
      image: isMobile
        ? `/static-page/guide-tips/how01-mobile.jpg`
        : `/static-page/guide-tips/how01.jpg`,
      titleKey: 'guideItems.measurePD.title',
      descriptionKey: 'guideItems.measurePD.description',
    },
    {
      path: '/guide-tips/face-shape',
      image: isMobile
        ? `/static-page/guide-tips/how02-mobile.jpg`
        : `/static-page/guide-tips/how02.jpg`,
      titleKey: 'guideItems.faceShape.title',
      descriptionKey: 'guideItems.faceShape.description',
    },
    {
      path: '/guide-tips/frame-size',
      image: frameSizeImages[env.NEXT_PUBLIC_SITE_NAME] || frameSizeImages.en,
      titleKey: 'guideItems.frameSize.title',
      descriptionKey: 'guideItems.frameSize.description',
    },
    {
      path: '/guide-tips/fill-out-prescription',
      image: prescriptionImages[env.NEXT_PUBLIC_SITE_NAME] || prescriptionImages.en,
      titleKey: 'guideItems.prescription.title',
      descriptionKey: 'guideItems.prescription.description',
    },
    {
      path: '/guide-tips/lenses-coating',
      image: `/static-page/guide-tips/how05.jpg`,
      titleKey: 'guideItems.lensesCoating.title',
      descriptionKey: 'guideItems.lensesCoating.description',
    },
    {
      path: '/guide-tips/bluelight',
      image: `/static-page/guide-tips/how06.jpg`,
      titleKey: 'guideItems.bluelight.title',
      descriptionKey: 'guideItems.bluelight.description',
    },
  ];

  return (
    <div className="container mx-auto">
      <div className="w-full md:w-4/5 mx-auto">
        <h1 className="text-3xl font-bold md:mb-28 font-bb">{t('title')}</h1>
        <p className="text-sm md:text-base font-ir pt-20 pb-40 md:pt-0 md:pb-0">
          {t('description')}
        </p>
        <div className="grid gap-20 md:gap-40 grid-cols-1 md:grid-cols-2 w-full mb-20 md:mt-40 md:mx-auto">
          {guideItems.map((item) => (
            <Link
              prefetch={false}
              href={item.path}
              key={item.path}
              className="bg-gray-100 md:bg-transparent py-24 px-16 md:px-0 overflow-hidden cursor-pointer"
            >
              <div className="flex items-center h-full md:h-auto flex-row text-center md:flex-col md:text-left">
                <div className="flex-shrink-0 w-2/5 h-full md:w-full flex items-start justify-center">
                  <Image
                    src={item.image}
                    alt={t(item.titleKey as any)}
                    className="w-full object-cover"
                    width={200}
                    height={120}
                  />
                </div>
                <div className="flex-1 ml-16 md:ml-0 md:mt-24">
                  <h2 className="text-[16px] md:text-2xl font-bb text-black mb-8 text-left">
                    {t(item.titleKey as any)}
                  </h2>
                  <p className="text-sm md:text-base text-auxiliary-text text-left">
                    {t(item.descriptionKey as any)}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChooseEyeglassesClient;
